import { Avatar } from '@/components/Avatar';
import { Button } from '@/ui/Button';
import { Dialog } from '@/ui/Dialog';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoVacio } from '@/components/EstadoVacio';
import { FalloDePanel } from '@/components/Panel';
import { REACCIONES } from './piezas';
import { useReacciones } from './api';

/** El icono que le corresponde a cada tipo de reacción en la lista. */
const ICONO: Record<string, string> = Object.fromEntries(
  REACCIONES.map((r) => [r.clave, r.activo]),
);

/**
 * Reacciones — quién reaccionó a una publicación.
 *
 * Diálogo y no panel: es una consulta puntual y corta, no se sigue trabajando
 * con el muro mientras se lee.
 *
 * La nota del pie no es decorativa: esta lista está disponible para la auditoría
 * de Bienestar y de Administración, y quien reacciona tiene derecho a saberlo.
 */
export function Reacciones({ postId, onCerrar }: { postId: number; onCerrar: () => void }) {
  const consulta = useReacciones(postId, true);

  return (
    <Dialog
      abierto
      onCambio={onCerrar}
      titulo="Reacciones"
      ancho="max-w-md"
      pie={
        <Button variant="ghost" onClick={onCerrar}>
          Cerrar
        </Button>
      }
    >
      {consulta.isPending ? (
        <Skeleton className="h-24" />
      ) : consulta.isError ? (
        <FalloDePanel
          que="las reacciones"
          err={consulta.error}
          onReintentar={() => void consulta.refetch()}
        />
      ) : !consulta.data?.length ? (
        <EstadoVacio icono="bi-emoji-neutral" titulo="Nadie ha reaccionado aún" compacto />
      ) : (
        <>
          <ul className="flex max-h-90 flex-col overflow-y-auto">
            {consulta.data.map((r, i) => (
              <li
                key={`${r.full_name}-${i}`}
                className="flex items-center gap-2.5 border-b border-line py-2 last:border-b-0"
              >
                <Avatar nombre={r.full_name} tamano={36} />
                <span className="min-w-0 flex-1 truncate text-3 font-semibold text-ink">
                  {r.full_name}
                </span>
                <i
                  className={`bi ${ICONO[r.reaction_type] ?? ICONO.like} grid size-6 shrink-0
                              place-items-center rounded-full bg-brand text-2 text-on-brand`}
                  aria-label={
                    REACCIONES.find((x) => x.clave === r.reaction_type)?.etiqueta ?? 'Me gusta'
                  }
                  role="img"
                />
              </li>
            ))}
          </ul>
          <p className="mt-3 text-center text-1 text-muted">
            <i className="bi bi-shield-check" aria-hidden="true" /> Disponible para auditoría de
            Bienestar y Administración.
          </p>
        </>
      )}
    </Dialog>
  );
}
